/* Copyright (c) 2018-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError, ReplaySubject, combineLatest, forkJoin, BehaviorSubject } from 'rxjs';
import { catchError, mergeMap, map, mapTo, take, tap, switchMap, filter } from 'rxjs/operators';
import { get, setWith, unset } from 'lodash';
import { BridgeService, BridgeError } from 'crimson/lib/bridge/bridge.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { NativeEventService } from 'crimson/lib/native-events/native-events.service';
import { NativeEvents } from 'crimson/lib/native-events/native-events';
import { EventHandler } from 'crimson/lib/events-handler/events-handler';
import { Commands } from 'bridge-commands/commands';
import { Types } from 'bridge-commands/types';
import { IndexedDBUtilService } from '../utils/indexedDbUtilService';
import { MigrationState, STORAGE_CONFIG, StorageConfig } from './shared-storage.models';

const migrateKeyPath = ['migrationState'];
@Injectable({
    providedIn: 'root'
})
export class SharedStorageService {
    private logger: Logger;
    private cachedSharedStorage$: ReplaySubject<Types.Common.Sharedstorage.CacheReturn>;
    private isInitialized: boolean;
    private shouldPerformJSONFileRead: boolean = false;
    private shouldPerformMigration: boolean = false;
    private registerStorageChangeEvent: boolean = false;
    private persistToJSONKeys: string[] = [];
    private migrationStateSubject = new ReplaySubject<MigrationState>(1);
    private initialReadDoneSubject = new BehaviorSubject<boolean>(false);

    constructor(
        private bridgeService: BridgeService,
        loggingService: LoggingService,
        private nativeEventService: NativeEventService,
        private indexedDBUtilService: IndexedDBUtilService,
        @Optional() @Inject(STORAGE_CONFIG) storageConfig: StorageConfig
    ) {
        this.logger = loggingService.getLogger('libraries/shared-storage');
        try {
            this.indexedDBUtilService.initialize(storageConfig?.objectStoreName);
        } catch (error) {
            this.logger.error('Error initializing IndexedDBUtilService ', error);
        }

        if (storageConfig) {
            this.logger.info('storageConfig ', storageConfig);
            this.shouldPerformJSONFileRead = storageConfig.shouldPerformJSONFileRead ?? false;
            this.shouldPerformMigration = storageConfig.shouldPerformMigration ?? false;
            this.registerStorageChangeEvent = storageConfig.registerStorageChangeEvent ?? false;
        }

        this.initializeStorageConfiguration(storageConfig);
        this.cachedSharedStorage$ = new ReplaySubject(1);
        this.isInitialized = false;
    }

    initialize() {
        this.logger.info('init');

        // Observable for getting Indexed DB shared storage cached data
        const getIndexedDBCache$ = this.indexedDBUtilService.getCacheOfSharedStorage().pipe(
            catchError(err => {
                this.logger.error(`Error while reading IndexedDB, ${err}`);
                return of({});
            })
        );

        if (!this.shouldPerformJSONFileRead) {
            // Apps that don't need to read from JSON file => read from IndexedDB only
            getIndexedDBCache$.pipe(take(1)).subscribe(indexedDBCacheddata => {
                this.cachedSharedStorage$.next(indexedDBCacheddata);
                this.initialReadDoneSubject.next(true);
            });
        } else {
            // Apps that need to read from JSON file => read from both JSON and IndexedDB
            // Observable for getting JSON shared storage cached data (only used for GFN desktop platforms)
            const getJSONCachedData$ = this.bridgeService.executeCommand(Commands.Common.Sharedstorage.Cache).pipe(
                catchError(error => {
                    this.logger.error('Error getting cached JSON data', error);
                    return of({});
                })
            );

            forkJoin([getJSONCachedData$, getIndexedDBCache$]).subscribe(([jsonData, indexedDBData]) => {
                this.cachedSharedStorage$.next(this.mergeStorageData(jsonData, indexedDBData));
                this.initialReadDoneSubject.next(true);
            });
        }

        if (this.registerStorageChangeEvent) {
            this.initialReadDoneSubject
                .asObservable()
                .pipe(
                    filter(value => value),
                    mergeMap(_ => {
                        return this.nativeEventService.get$(
                            NativeEvents.SharedStorageUpdate,
                            EventHandler.Types.HandlerEventType.StructuredWindowEvent
                        );
                    }),
                    map((stringData: string) => JSON.parse(stringData)),
                    switchMap((data: Types.Common.Sharedstorage.CacheReturn) => {
                        if (!this.shouldPerformJSONFileRead) {
                            return of(data);
                        }

                        return combineLatest([of(data), getIndexedDBCache$]).pipe(
                            map(([jsonData, indexedDBData]) => {
                                return this.mergeStorageData(jsonData, indexedDBData);
                            })
                        );
                    }),
                    catchError(error => {
                        this.logger.error('error receiving native event', error);
                        return this.cachedSharedStorage$.pipe(take(1));
                    })
                )
                .subscribe((sharedStorageCache: Types.Common.Sharedstorage.CacheReturn) => {
                    this.logger.info('shared storage cache updated');
                    this.cachedSharedStorage$.next(sharedStorageCache);
                });
        }

        this.isInitialized = true;
    }

    private initializeStorageConfiguration(storageConfig: StorageConfig) {
        if (this.shouldPerformMigration) {
            this.persistToJSONKeys = storageConfig?.keysToPersistInJSON;
        } else {
            this.setMigrationState(MigrationState.NOT_APPLICABLE);
        }
    }

    private mergeStorageData(
        jsonData: Types.Common.Sharedstorage.CacheReturn,
        indexedDBData: Types.Common.Sharedstorage.CacheReturn
    ): Types.Common.Sharedstorage.CacheReturn {
        let storedData = jsonData ?? {};
        // wait and read the migration state from indexedDB, if it is SUCCESS, merge the indexedDB data into the JSON data
        //if there are overlapping keys, the indexedDB data will overwrite the JSON data
        if (indexedDBData?.migrationState?.state === MigrationState.SUCCESS) {
            storedData = { ...storedData, ...indexedDBData };
        }
        return storedData;
    }

    setMigrationState(migrationState: MigrationState) {
        this.migrationStateSubject.next(migrationState);
    }

    private getMigrationState$(): Observable<MigrationState> {
        return this.migrationStateSubject.asObservable();
    }

    /**
     * Reads the block with the specified name from the shared storage file
     *
     * @param path Path to the name of the block to read, spearated by a single slash
     * @returns The specified JSON block shared storage wrapped in an Observable. If no block exists,
     *          it will return an empty object wrapped in an Observable.
     */
    readBlock<T extends object>(path: string): Observable<T> {
        if (!this.isInitialized) {
            this.initialize();
        }
        return this.cachedSharedStorage$.pipe(
            take(1),
            mergeMap(cachedSharedStorage => {
                const ret = get(cachedSharedStorage, path.split('/'));
                if (ret === undefined || ret === null) {
                    return throwError({
                        code: 204
                    });
                }
                return of(ret as T);
            }),
            catchError((error: BridgeError<Types.Common.Sharedstorage.ReadParameters>) => {
                this.logReadError(path, error);
                return of({} as T);
            })
        );
    }

    private logReadError(path: string, error: BridgeError<Types.Common.Sharedstorage.ReadParameters>): void {
        switch (error.code) {
            case 404:
                this.logger.info('No shared storage file found, creating a new one.');
                break;
            case 204:
                this.logger.info(`No entry for ${path} exists. Defaulting to empty object.`);
                break;
            default:
                this.logger.error(
                    `Error (${error.code}) ${error.message}. ` + `Defaulting to empty object for path ${path}.`
                );
                break;
        }
    }

    /**
     * Writes a block to shared storage
     *
     * @param path Path to the name of the block to write, separated by a single slash
     * @param data the JSON object to write
     * @param wait If the process should wait for the result to be resolved.
     * @returns True or false wrapped in an Observable.
     */
    writeBlock(path: string, data: object, wait?: boolean): Observable<boolean> {
        const write$ = this.writeBlockInternal(path, data).pipe(
            take(1),
            catchError(error => {
                this.logger.error('error writing for path', path, error);
                return of(false);
            })
        );
        if (!this.isInitialized) {
            this.initialize();
        }
        return this.cachedSharedStorage$.pipe(
            take(1),
            mergeMap(cachedSharedStorage => {
                this.cachedSharedStorage$.next(setWith(cachedSharedStorage, path.split('/'), data, Object));
                if (wait) {
                    return write$;
                }
                write$.subscribe();
                return of(true);
            })
        );
    }

    //wait until migration state is resolved before writing to shared storage
    private writeBlockInternal(path: string, data: object): Observable<boolean> {
        return this.getMigrationState$().pipe(
            take(1),
            mergeMap(migrationState => {
                const keyPathPostSplit = path.split('/');
                if (this.shouldUseJSONStorage(keyPathPostSplit, migrationState)) {
                    this.logger.info('Writing to JSON shared storage for path:', path);
                    return this.bridgeService.executeCommand(Commands.Common.Sharedstorage.Write, {
                        path: keyPathPostSplit,
                        data
                    });
                }

                this.logger.info('Writing to IndexedDB for path:', path);
                return this.indexedDBUtilService.writeToSharedStorage(keyPathPostSplit, data);
            })
        );
    }

    /**
     * Removes the block with the specified name from the shared storage file
     *
     * @param path Path to the name of the block to removed, spearated by a single slash
     * @returns True if node removal was successful, otherwise false, wrapped in an Observable.
     */
    removeBlock(path: string): Observable<boolean> {
        if (!this.isInitialized) {
            this.initialize();
        }
        return this.cachedSharedStorage$.pipe(
            take(1),
            map(cachedSharedStorage => {
                const status = unset(cachedSharedStorage, path);
                this.cachedSharedStorage$.next(cachedSharedStorage);
                this.removeBlockInternal(path).subscribe();
                return status;
            })
        );
    }

    //wait until migration state is resolved before removing from shared storage
    private removeBlockInternal(path: string): Observable<boolean> {
        return this.getMigrationState$().pipe(
            take(1),
            mergeMap(migrationState => {
                const keyPathPostSplit = path.split('/');
                if (this.shouldUseJSONStorage(keyPathPostSplit, migrationState)) {
                    this.logger.info('Removing from JSON shared storage for path:', path);
                    return this.executeRemoveFromJSONSharedStore(path);
                }
                this.logger.info('Removing from IndexedDB for path:', path);
                return this.indexedDBUtilService.removeFromSharedStorage(keyPathPostSplit);
            })
        );
    }

    private executeRemoveFromJSONSharedStore(path: string): Observable<boolean> {
        return this.bridgeService
            .executeCommand(Commands.Common.Sharedstorage.Remove, {
                path: path.split('/')
            })
            .pipe(
                mapTo(true),
                catchError((error: BridgeError<Types.Common.Sharedstorage.RemoveParameters>) => {
                    switch (error.code) {
                        case 204:
                            this.logger.warn(`No entry for ${path} exists. Removal failed.`);
                            break;
                        default:
                            this.logger.error(`Error (${error.code}) ${error.message}.`);
                            break;
                    }
                    return of(false);
                })
            );
    }

    public getSharedStorageKeys(): Observable<string[]> {
        if (!this.isInitialized) {
            this.initialize();
        }

        return this.cachedSharedStorage$.pipe(
            take(1),
            map(cachedSharedStorage => {
                return Object.keys(cachedSharedStorage);
            })
        );
    }

    loadMigrationState(): Observable<MigrationState> {
        return this.indexedDBUtilService.readFromSharedStorage(migrateKeyPath).pipe(
            map(storedState => {
                if (storedState && storedState.state) {
                    return storedState.state;
                }
                return MigrationState.NOT_ATTEMPTED;
            }),
            catchError(err => {
                this.logger.error('Error loading migration state', err);
                return of(MigrationState.NOT_ATTEMPTED);
            })
        );
    }

    saveMigrationStateToIndexedDB(state: MigrationState): Observable<boolean> {
        return this.indexedDBUtilService.writeToSharedStorage(migrateKeyPath, { state }).pipe(
            catchError(err => {
                this.logger.error('Error saving migration state', err);
                return of(false);
            })
        );
    }

    /**
     * Initializes the shared storage migration.
     * @returns An Observable that emits when the migration is complete.
     */
    initializeMigration(): Observable<void> {
        if (!this.shouldPerformMigration) {
            return of(undefined);
        }

        return this.loadMigrationState().pipe(
            switchMap(migrationState => {
                if (migrationState === MigrationState.NOT_ATTEMPTED || migrationState === MigrationState.FAILED) {
                    return this.performMigration();
                }
                // Migration had already been done in previous updates
                this.setMigrationState(migrationState);
                return of(undefined); // Migration already done or failed
            })
        );
    }

    performMigration(): Observable<void> {
        this.logger.info('Running Shared Storage Migration ...');
        return this.migrateKeys().pipe(
            switchMap(success => {
                const migrationState = success ? MigrationState.SUCCESS : MigrationState.FAILED;
                if (!success) {
                    this.logger.error('Migration failed. Continuing with JSON file for shared storage...');
                }
                this.setMigrationState(migrationState);
                return this.saveMigrationStateToIndexedDB(migrationState);
            }),
            catchError(err => {
                this.logger.error('Error during shared storage migration', err);
                this.setMigrationState(MigrationState.FAILED);
                return this.saveMigrationStateToIndexedDB(MigrationState.FAILED);
            }),
            map(() => {})
        );
    }

    migrateKeys(): Observable<boolean> {
        const startTime = Date.now();
        // 1) Read all data from JSON file
        return this.bridgeService.executeCommand(Commands.Common.Sharedstorage.Cache).pipe(
            switchMap(cachedJSONData => {
                // 2) Build an array of per-key migration observables
                const indexDBWriteObservables: Observable<{ key: string; result: boolean }>[] = [];
                Object.keys(cachedJSONData).forEach(key => {
                    if (!this.persistToJSONKeys.includes(key)) {
                        indexDBWriteObservables.push(
                            this.indexedDBUtilService.writeToSharedStorage(key.split('/'), cachedJSONData[key]).pipe(
                                map(result => ({ key, result })),
                                catchError(error => {
                                    // If there's an error, emit result = false for that key
                                    this.logger.error(`Failed writing key '${key}' to IndexedDB`, error);
                                    return of({ key, result: false });
                                })
                            )
                        );
                    }
                });

                // If there are no keys to migrate, return true immediately
                if (indexDBWriteObservables.length === 0) {
                    return of(true);
                }

                return combineLatest(indexDBWriteObservables).pipe(
                    take(1),
                    map(results => {
                        // Check if every key write was successful
                        const allSucceeded = results.every(r => r.result === true);
                        if (allSucceeded) {
                            results.forEach(({ key }) => {
                                //If write was successful, fire-and-forget command for removal from JSON
                                this.executeRemoveFromJSONSharedStore(key).subscribe();
                            });
                        }
                        this.logger.info('Execution time for migration script (ms): ', Date.now() - startTime);
                        this.isInitialized = false; //need to reinitialize cache after migration
                        return allSucceeded;
                    })
                );
            }),
            catchError(error => {
                this.logger.error('Error migrating shared storage to IndexedDB', error);
                // Fail the entire migration
                return of(false);
            })
        );
    }

    private shouldUseJSONStorage(keyPath: string[], migrationState: MigrationState): boolean {
        return (
            this.shouldPerformJSONFileRead &&
            (migrationState === MigrationState.FAILED ||
                migrationState === MigrationState.NOT_ATTEMPTED ||
                migrationState === MigrationState.NOT_APPLICABLE ||
                (migrationState === MigrationState.SUCCESS && this.persistToJSONKeys.includes(keyPath[0])))
        );
    }
}
